import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { HelperText } from '../helper-text/HelperText'
import { LoginModalTypeEnum } from '../../login.enum'
import { LoginModalSubscribeFormProps } from '../../login.types'
import { SsoLogin } from '../../sso-login/SsoLogin'
import { cn } from '../../../../../shadcn/utils'
import regex from '../../../../../utils/regex'
import { Button, BUTTON_COLOR } from '../../../../atoms'
import { InputText } from '../../../../molecules'

export interface InputsSubscribe {
  firstname: string
  lastname: string
  email: string
  password: string
}

const TEST_ID_PREFIX = 'login-modal-sign-up'

const TermsAndConditionLinks = ({ className }: { className?: string }) => (
  <p className={cn(s.termsAndConditions, className)}>
    <FormattedMessage
      id='ui.LoginModalSubscribeForm.acceptTerms'
      defaultMessage="En cliquant sur Accepter et s'inscrire, vous acceptez les {termsLink}, la {privacyLink} de Naboo."
      values={{
        termsLink: (
          <a
            data-gtm='footer_conditions'
            className={s.link}
            href='/conditions-generales'
            target='_blank'
            rel='noreferrer noopener'
          >
            <FormattedMessage
              id='ui.LoginModalSubscribeForm.termLink'
              defaultMessage="Conditions Générales d'Utilisation"
            />
          </a>
        ),
        privacyLink: (
          <a
            data-gtm='footer_mentions'
            className={s.link}
            href='/confidentialite'
            target='_blank'
            rel='noreferrer noopener'
          >
            <FormattedMessage
              id='ui.LoginModalSubscribeForm.privacyLink'
              defaultMessage='Politique de confidentialité'
            />
          </a>
        ),
      }}
    />
  </p>
)

export const LoginModalSubscribeForm = ({
  isLoading,
  email,
  control,
  isFormValid,
  handleOnSubmit,
  helperText,
  setModalType,
  ssoOptions,
  fromMarketplace = false,
}: LoginModalSubscribeFormProps) => {
  const intl = useIntl()
  const fieldClassName = fromMarketplace
    ? 'rounded-[5px] !px-5 !py-4 text-sm leading-none'
    : undefined

  return (
    <>
      <form className={s.form} onSubmit={handleOnSubmit}>
        <div className={cn(s.name, fromMarketplace && '!flex-row')}>
          <div className='w-full'>
            <InputText
              control={control}
              name='firstname'
              label={
                <FormattedMessage
                  id='ui.LoginModalSubscribeForm.label.firstname'
                  defaultMessage='Prénom'
                />
              }
              rules={{
                required: intl.formatMessage({
                  id: 'ui.LoginModalSubscribeForm.firstnameRequired',
                  defaultMessage: 'Prénom requis.',
                }),
              }}
              labelProps={{ 'aria-required': 'true' }}
              inputProps={{
                type: 'text',
                'aria-label': intl.formatMessage({
                  id: 'ui.LoginModalSubscribeForm.placeholder.firstname',
                  defaultMessage: 'Votre prénom',
                }),
                'aria-required': 'true',
                className: fieldClassName,
              }}
              placeholder={intl.formatMessage({
                id: 'ui.LoginModalSubscribeForm.placeholder.firstname',
                defaultMessage: 'Votre prénom',
              })}
              dataTestId={`${TEST_ID_PREFIX}-first-name-input`}
            />
          </div>
          <div className='w-full'>
            <InputText
              control={control}
              name='lastname'
              label={
                <FormattedMessage
                  id='ui.LoginModalSubscribeForm.label.lastname'
                  defaultMessage='Nom'
                />
              }
              labelProps={{ 'aria-required': 'true' }}
              rules={{
                required: intl.formatMessage({
                  id: 'ui.LoginModalSubscribeForm.lastnameRequired',
                  defaultMessage: 'Nom requis.',
                }),
              }}
              inputProps={{
                type: 'text',
                'aria-label': intl.formatMessage({
                  id: 'ui.LoginModalSubscribeForm.placeholder.lastname',
                  defaultMessage: 'Votre nom',
                }),
                'aria-required': 'true',
                className: fieldClassName,
              }}
              placeholder={intl.formatMessage({
                id: 'ui.LoginModalSubscribeForm.placeholder.lastname',
                defaultMessage: 'Votre nom',
              })}
              dataTestId={`${TEST_ID_PREFIX}-last-name-input`}
            />
          </div>
        </div>
        <div className='pt-1 md:pt-0'>
          <InputText
            control={control}
            name='email'
            label={
              <FormattedMessage
                id='ui.LoginModalSubscribeForm.label.email'
                defaultMessage='E-mail professionnel'
              />
            }
            labelProps={{
              'aria-required': 'true',
            }}
            rules={{
              validate: {
                required: (email: string) =>
                  !!email ||
                  intl.formatMessage({
                    id: 'ui.LoginModalSubscribeForm.emailRequired',
                    defaultMessage: 'Une adresse e-mail est requise.',
                  }),
                email: (email: string) =>
                  regex.email.test(email) ||
                  intl.formatMessage({
                    id: 'ui.LoginModalSubscribeForm.emailWrongFormat',
                    defaultMessage: "Respecter le bon format d'e-mail",
                  }),
              },
            }}
            inputProps={{
              type: 'text',
              'aria-label': intl.formatMessage({
                id: 'ui.LoginModalSubscribeForm.placeholder.email',
                defaultMessage: 'Entrer votre adresse e-mail professionnelle',
              }),
              'aria-required': 'true',
              className: fieldClassName,
            }}
            placeholder={intl.formatMessage({
              id: 'ui.LoginModalSubscribeForm.placeholder.email',
              defaultMessage: 'Entrer votre adresse e-mail professionnelle',
            })}
            disabled={!!email}
            dataTestId={`${TEST_ID_PREFIX}-email-input`}
          />
        </div>
        <div className={cn('pt-1 md:pt-0', fromMarketplace && '!pt-1')}>
          <InputText
            control={control}
            name='password'
            label={
              <FormattedMessage
                id='ui.LoginModalSubscribeForm.label.password'
                defaultMessage='Mot de passe'
              />
            }
            labelProps={{
              'aria-required': 'true',
            }}
            rules={{
              validate: (password: string | undefined) =>
                password && password.length >= 6
                  ? true
                  : intl.formatMessage({
                      id: 'ui.LoginModalSubscribeForm.passwordMinLength',
                      defaultMessage: 'Minimum 6 caractères',
                    }),
            }}
            inputProps={{
              type: 'password',
              'aria-label': intl.formatMessage({
                id: 'ui.LoginModalSubscribeForm.placeholder.password',
                defaultMessage: 'Choisir votre mot de passe',
              }),
              'aria-required': 'true',
              className: fieldClassName,
            }}
            placeholder={intl.formatMessage({
              id: 'ui.LoginModalSubscribeForm.placeholder.password',
              defaultMessage: 'Choisir votre mot de passe',
            })}
            dataTestId={`${TEST_ID_PREFIX}-password-input`}
          />
        </div>

        <>
          {helperText ? (
            <HelperText
              {...helperText}
              data-testid={`${TEST_ID_PREFIX}-helper-text`}
            />
          ) : null}

          <Button
            id='finish-subscribe'
            className={cn('mt-5', fromMarketplace && 'mt-6')}
            loading={isLoading}
            disabled={!isFormValid}
            fullWidth={true}
            data-testid={`${TEST_ID_PREFIX}-submit-button`}
            type='submit'
          >
            <FormattedMessage
              id='ui.LoginModalSubscribeForm.confirmSubscribe'
              defaultMessage="Accepter et s'inscrire"
            />
          </Button>

          {fromMarketplace && <TermsAndConditionLinks className='!mt-6' />}

          <SsoLogin {...ssoOptions} />

          {!fromMarketplace && <TermsAndConditionLinks />}
        </>
      </form>
      <div className={cn(s.footer, fromMarketplace && '!flex !justify-center')}>
        <FormattedMessage
          id='ui.LoginModalSubscribeForm.footerAlreadyHaveAccount'
          defaultMessage="'Vous avez déjà un compte ?"
        />
        {'\u00A0'}
        <Button
          onClick={() => setModalType({ type: LoginModalTypeEnum.Login })}
          color={BUTTON_COLOR.LINK}
          type='button'
        >
          <FormattedMessage
            id='ui.LoginModalSubscribeForm.footerLogin'
            defaultMessage='Connectez-vous'
          />
        </Button>
      </div>
    </>
  )
}
